import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ShieldAlert, Hourglass, Gauge, AlertTriangle } from 'lucide-react'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { useAnalysisStats } from '../hooks/useAnalysis'

/** Shared with RISK_BADGE in CopyrightReviewList — same palette, same meaning. */
const RISK_COLOR: Record<string, string> = {
  critical: 'bg-red-600',
  high: 'bg-red-400',
  medium: 'bg-amber-400',
  low: 'bg-green-500',
  unknown: 'bg-muted-foreground/40',
  unassessed: 'bg-muted-foreground/20',
}

const RISK_ORDER = ['critical', 'high', 'medium', 'low', 'unknown', 'unassessed']

function StatCard({
  title,
  value,
  subtitle,
  icon,
  tone,
}: {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ReactNode
  tone?: 'danger' | 'warning' | 'good'
}) {
  const toneClass =
    tone === 'danger'
      ? 'border-red-200 bg-red-50/50'
      : tone === 'warning'
        ? 'border-amber-200 bg-amber-50/40'
        : tone === 'good'
          ? 'border-green-200 bg-green-50/40'
          : ''

  return (
    <Card className={toneClass}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-medium font-sans text-muted-foreground">
          {title}
        </CardTitle>
        <div className="text-muted-foreground/70">{icon}</div>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="text-4xl font-serif font-semibold tracking-tight">{value}</div>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </CardContent>
    </Card>
  )
}

/** Horizontal stacked bar. Deliberately not a pie: these are parts of one
 *  queue, and the eye compares lengths far better than angles. */
function RiskBar({ data, total }: { data: { risk_level: string; count: number }[]; total: number }) {
  if (!total) return null
  const ordered = [...data].sort(
    (a, b) => RISK_ORDER.indexOf(a.risk_level) - RISK_ORDER.indexOf(b.risk_level)
  )

  return (
    <div className="space-y-3">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
        {ordered.map((d) => (
          <div
            key={d.risk_level}
            className={RISK_COLOR[d.risk_level] || RISK_COLOR.unknown}
            style={{ width: `${(d.count / total) * 100}%` }}
            title={`${d.risk_level}: ${d.count}`}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
        {ordered.map((d) => (
          <div key={d.risk_level} className="flex items-center gap-2 text-sm">
            <span
              className={`h-2.5 w-2.5 shrink-0 rounded-sm ${RISK_COLOR[d.risk_level] || RISK_COLOR.unknown}`}
            />
            <span className="capitalize text-muted-foreground">{d.risk_level}</span>
            <span className="ml-auto font-medium tabular-nums">{d.count}</span>
            <span className="w-12 text-right text-xs text-muted-foreground tabular-nums">
              {((d.count / total) * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Sparkline-style column chart of analyses completed per day. */
function ThroughputChart({ data }: { data: { date: string; count: number }[] }) {
  if (!data.length) {
    return <p className="text-sm text-muted-foreground">No analyses in the last 14 days.</p>
  }
  const max = Math.max(...data.map((d) => d.count))

  return (
    <div className="flex h-28 items-end gap-1.5">
      {data.map((d) => (
        <div key={d.date} className="group flex flex-1 flex-col items-center gap-1">
          <span className="text-[10px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 tabular-nums">
            {d.count}
          </span>
          <div
            className="w-full rounded-t bg-arthaus-gold/70 transition-colors group-hover:bg-arthaus-gold"
            style={{ height: `${Math.max((d.count / max) * 100, 3)}%` }}
            title={`${d.date}: ${d.count}`}
          />
          <span className="text-[10px] text-muted-foreground">{d.date.slice(5)}</span>
        </div>
      ))}
    </div>
  )
}

export function CopyrightDashboard() {
  const { data: stats, isLoading, error } = useAnalysisStats()

  if (isLoading) return <LoadingSpinner />
  if (error || !stats) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Could not load copyright statistics.</AlertDescription>
      </Alert>
    )
  }

  const { funnel, riskDistribution, throughput, confidence } = stats
  const coverage = funnel.pending_total
    ? Math.round((funnel.analyzed / funnel.pending_total) * 100)
    : 0
  const flagged = funnel.blocked_by_risk
  const avgConfidence = confidence.avg_confidence ? Number(confidence.avg_confidence) : null

  return (
    <div className="space-y-6">
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Analysis Coverage"
          value={`${coverage}%`}
          subtitle={`${funnel.analyzed} of ${funnel.pending_total} pending analyzed`}
          icon={<Gauge className="h-5 w-5" />}
          tone={coverage < 50 ? 'warning' : 'good'}
        />
        <StatCard
          title="Flagged for Review"
          value={flagged}
          subtitle="medium risk and above"
          icon={<ShieldAlert className="h-5 w-5" />}
          tone={flagged > 0 ? 'danger' : undefined}
        />
        <StatCard
          title="Awaiting Analysis"
          value={funnel.unassessed}
          subtitle="never auto-publishes while unassessed"
          icon={<Hourglass className="h-5 w-5" />}
        />
        <StatCard
          title="Mean Confidence"
          value={avgConfidence !== null ? `${(avgConfidence * 100).toFixed(0)}%` : '—'}
          subtitle={`across ${confidence.scored} scored analyses`}
          icon={<Gauge className="h-5 w-5" />}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl">Risk Distribution</CardTitle>
            <p className="text-sm text-muted-foreground">
              Latest analysis per pending artwork
            </p>
          </CardHeader>
          <CardContent>
            <RiskBar data={riskDistribution} total={funnel.pending_total} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl">Analysis Throughput</CardTitle>
            <p className="text-sm text-muted-foreground">Completed per day, last 14 days</p>
          </CardHeader>
          <CardContent>
            <ThroughputChart data={throughput} />
          </CardContent>
        </Card>
      </div>

    </div>
  )
}
